/// <reference types="user-agent-data-types" />

export enum OperatingSystem {
  Android = 'Android',
  iOS = 'iOS',
  Linux = 'Linux',
  Mac = 'Mac',
  Unknown = 'Unknown',
  Windows = 'Windows',
}
export const getCurrentOS = () => {
  /*
   * Combine every signal into one haystack so we don't have to pick a single
   * "winner" source — each source is unreliable in a different way:
   *   - navigator.userAgentData.platform: missing in some Chromium builds
   *   - navigator.platform: reports "Linux armv81" on Android (no "android")
   *   - navigator.userAgent: reliable for mobile, spoofable on desktop
   * Order of checks matters: mobile tokens must come before desktop tokens
   * because the Android UA contains "Linux; Android" and the iPad
   * "Request Desktop Website" UA contains "Macintosh".
   */
  const haystack = [
    navigator.userAgentData?.platform ?? '',
    navigator.platform,
    navigator.userAgent,
  ]
    .join(' ')
    .toLowerCase()

  if (haystack.includes('android')) {
    return OperatingSystem.Android
  }
  if (haystack.includes('iphone') || haystack.includes('ipad')) {
    return OperatingSystem.iOS
  }
  if (haystack.includes('win')) {
    return OperatingSystem.Windows
  }
  if (haystack.includes('mac')) {
    // iPads in "Request Desktop Website" mode report as Mac; use touch support to detect them.
    if (navigator.maxTouchPoints > 0) {
      return OperatingSystem.iOS
    }
    return OperatingSystem.Mac
  }
  if (haystack.includes('linux')) {
    return OperatingSystem.Linux
  }
  return OperatingSystem.Unknown
}

export const getIsIOS = () => getCurrentOS() === OperatingSystem.iOS

export const getIsAndroid = () => getCurrentOS() === OperatingSystem.Android

export const getIsDesktopOS = () => {
  const currentOS = getCurrentOS()
  return [OperatingSystem.Windows, OperatingSystem.Mac, OperatingSystem.Linux].includes(currentOS)
}

export const getIsMobileOS = () => !getIsDesktopOS()

/**
 * Display label for the OS-appropriate keyboard modifier key. Mac shows `⌘`,
 * every other desktop platform (Windows / Linux / unrecognised) shows `Ctrl`,
 * and mobile platforms return an empty string so callers can omit the hint —
 * touch devices have no keyboard modifier to surface. Mirrors the OS branch
 * in `hasModifierKeyPressed`.
 */
export const getModifierKey = () => {
  const currentOS = getCurrentOS()
  if (currentOS === OperatingSystem.Mac) {
    return '⌘'
  }
  if (currentOS === OperatingSystem.Android || currentOS === OperatingSystem.iOS) {
    return ''
  }
  return 'Ctrl'
}

/**
 * Check if the OS-appropriate modifier key is pressed in an event.
 * On Mac, this checks for the Cmd (meta) key.
 * On Windows/Linux, this checks for the Ctrl key.
 */
export const hasModifierKeyPressed = (event: { metaKey: boolean; ctrlKey: boolean }) => {
  const isMac = getCurrentOS() === OperatingSystem.Mac
  return isMac ? event.metaKey : event.ctrlKey
}

export const getIsFirefox = () => {
  if (!navigator?.userAgent) {
    return false
  }
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1
}
